import * as z from 'zod';

const addressSchema = z.object({
  address_type: z.enum(['billing', 'shipping']),
  addressLine1: z
    .string()
    .nonempty('Please enter address line 1.')
    .min(4, 'Address line 1 must contain at-least 4 characters.')
    .max(255, 'Address line 1 cannot exceed 255 characters'),
  addressLine2: z.string().max(255, 'Address line 2 cannot exceed 255 characters').optional().or(z.literal('')),
  city: z.string().min(2, 'Please enter a valid city').max(50, 'City must be at most 50 characters').nonempty('Please enter a valid city'),
  state: z.string().min(2, 'Please select a state').nonempty('Please select a state'),
  postalCode: z.string().regex(/^\d{5}$/, 'Postal code must be 5 digits').nonempty('Please enter a valid postal code'),
}).required();

// Public patient form schema - excludes administrative fields (status, practitioner_id, assignedClinic, note)
export const patientFormPublicSchema = z.object({
  first_name: z
    .string()
    .nonempty('Please enter first name.')
    .min(2, 'First must contain at-least 2 characters.'),
  middle_name: z.string().optional(),
  last_name: z
    .string()
    .nonempty('Please enter last name.')
    .min(2, 'Last name must contain at-least 2 characters.'),
  gender: z.string().nonempty('Please select a gender.'),
  dob: z.string().nonempty('Please select a date of birth.'),
  species: z.string().default('human'),
  animal_owner_name: z.string().optional(),
  weight: z.string().optional(),
  weight_recorded_date: z.string().optional(),
  height: z.number().min(0, 'Height must be positive').max(120, 'Height must be less than 120 inches').optional(),
  blood_pressure_systolic: z.union([z.number().int().min(50, 'Systolic must be at least 50').max(300, 'Systolic must be at most 300'), z.literal('')]).optional(),
  blood_pressure_diastolic: z.union([z.number().int().min(20, 'Diastolic must be at least 20').max(200, 'Diastolic must be at most 200'), z.literal('')]).optional(),
  blood_pressure_recorded_at: z.string().optional(),
  medication_allergies: z.array(z.string().max(100, 'Allergy name too long')).optional(),
  no_known_allergies: z.boolean().optional(),
  medical_history: z.string().max(5000, 'Medical history cannot exceed 5000 characters').optional(),
  preferred_contact_methods: z.object({
    email: z.boolean().optional(),
    sms: z.boolean().optional(),
    phone: z.boolean().optional(),
  }).optional(),
  emergency_contact_phone: z.string().optional(),
  insurance_carrier: z.string().max(100, 'Insurance carrier cannot exceed 100 characters').optional(),
  insurance_member_id: z.string().max(100, 'Member ID cannot exceed 100 characters').optional(),
  insurance_group_id: z.string().max(100, 'Group ID cannot exceed 100 characters').optional(),
  contacts: z.array(
    z
      .object({
        id: z.string().optional(),
        key: z.string().optional(),
        value: z.string().optional(),
        is_default: z.boolean().optional(),
      })
      .superRefine((data, ctx) => {
        const contact = data.value || '';

        if (data.key === 'phone') {
          if (contact && !/^\+1\d{10}$/.test(contact)) {
            ctx.addIssue({
              path: ['value'],
              code: z.ZodIssueCode.custom,
              message: 'Please enter a valid US phone number in the format +1XXXXXXXXXX',
            });
          }
        }

        if (data.key === 'email') {
          if (contact && !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(contact)) {
            ctx.addIssue({
              path: ['value'],
              code: z.ZodIssueCode.custom,
              message: 'Please enter a valid email address',
            });
          }
        }
      })
  ),
  shipping_same_as_billing: z.boolean().default(true),
  addresses: z.array(addressSchema).min(1).max(2),
}).superRefine((data, ctx) => {
  // Validate that either allergies are selected OR "no known allergies" is checked
  const hasAllergies = data.medication_allergies && data.medication_allergies.length > 0;
  const noKnownAllergies = data.no_known_allergies === true;

  if (!hasAllergies && !noKnownAllergies) {
    ctx.addIssue({
      path: ['medication_allergies'],
      code: z.ZodIssueCode.custom,
      message: 'Please select allergies or check "No Known Allergies"',
    });
  }

  // If "no known allergies" is checked, allergies should be empty
  if (noKnownAllergies && hasAllergies) {
    ctx.addIssue({
      path: ['medication_allergies'],
      code: z.ZodIssueCode.custom,
      message: 'Cannot have allergies selected when "No Known Allergies" is checked',
    });
  }

  // Validate addresses: must have exactly one billing
  const billingAddresses = data.addresses.filter(a => a.address_type === 'billing');
  if (billingAddresses.length !== 1) {
    ctx.addIssue({
      path: ['addresses'],
      code: z.ZodIssueCode.custom,
      message: 'Exactly one billing address is required.',
    });
  }

  // If shipping is not same as billing, must have a shipping address
  if (!data.shipping_same_as_billing) {
    const shippingAddresses = data.addresses.filter(a => a.address_type === 'shipping');
    if (shippingAddresses.length !== 1) {
      ctx.addIssue({
        path: ['addresses'],
        code: z.ZodIssueCode.custom,
        message: 'A shipping address is required when not using billing address for shipping.',
      });
    }
  }
});
